// The playhead driver (sources-v2 D14): every surface reads, writes and seeks the on-stage file through here.
import { bandLevel, needAudio } from '~/lib/audio/bands'
import { followLevel } from '~/lib/automation/engine'
import {
	cyclesAtPosition,
	ONE_SHOT_CYCLES,
	positionAt,
	positionOfValue,
	sampleEnvelope,
} from '~/lib/automation/evaluate'
import { onTick } from '~/lib/clock'
import tempo from '~/lib/clock/tempo'
import { IS_DEV } from '~/lib/constants'
import { sourceValues } from '~/lib/params'
import video_file_schema, { FILE_CAPABILITIES } from '~/schemas/video_file_schema'
import store from '~/store'
import { setHasEnded, setLooping, setMuted as setMutedFlag } from '~/store/videoFile'
import { fillPictures } from './fillSources'
import * as pool from './videoPool'

// The pool node `startPlayhead` follows (fileStage's transport).
let NODE = null
// About one frame at 25fps: `currentTime` never lands exactly on `out`.
const END_EPSILON_S = 0.04

/** The fill source on stage that shows staged file `id` (its own id, or a preset's file matched here by fingerprint); null when no fill does. */
let shown = { objects: null, bags: null, device: null, byId: new Map() }
const sourceOf = id => {
	const { live, camera } = store.getState()
	if (shown.objects !== live.objects || shown.bags !== live.source_settings || shown.device !== camera.device) {
		const pictures = fillPictures(live.objects, { device: camera.device, bags: live.source_settings })
		shown = {
			objects: live.objects,
			bags: live.source_settings,
			device: camera.device,
			byId: new Map(pictures.filter(p => p.id).map(p => [p.id, p.source])),
		}
	}
	return shown.byId.get(id) ?? null
}

// A preset load onto another file, keyed by local id: its settings until Redux names that file (a fade settles later), and the seek to its `start`.
const cues = new Map()
const seeks = new Set()
// The settings each file last played under, kept while no fill names it (a cut's first frame).
const lastSettings = new Map()
// Same-file fade: values the tween carries; the fade's settle clears them.
let override = {}
// A drag's uncommitted params, over the file's own until `commitParams` writes them.
let draft = null
const draftListeners = new Set()
// A one-shot that reached its end stays paused there though `run` is still true.
let doneId = null
// The pass the playhead is on: 1 toward out, -1 toward in. Runtime only; restarts with the file, its direction or its mode (`passKey`).
let pass = 1
let passKey = null
// A clock-driven reverse pass: where the playhead is, ahead of the element's seeks.
let head = 0
let lastTickAt = null
let offClock = null
// A shaped pass: cycles along in→out (null: read from the playhead on the next tick), and the position it last played.
let cycles = null
let lastPos = null

const initial = key => video_file_schema[key].initial

// What the staged file plays at, drafts and fades included: the transport reads this, never the element.
const IDLE = Object.freeze({ rate: initial('rate'), volume: initial('volume') })
let playing = IDLE
const playingListeners = new Set()

const publish = p => {
	const next = p ? { rate: p.rate, volume: p.volume } : IDLE
	if (next.rate === playing.rate && next.volume === playing.volume) return
	playing = next
	for (const fn of playingListeners) fn()
}

/** `{ rate, volume }` of the staged file, volume in percent; re-read on `onPlaying`. */
export const currentPlaying = () => playing

export const onPlaying = fn => {
	playingListeners.add(fn)
	return () => playingListeners.delete(fn)
}

/** The loop params governing a staged file: a pending cue's, else its fill source's bag while a fill shows it, else what it last played under. */
const settingsFor = id => {
	const source = sourceOf(id)
	const cue = cues.get(id)
	if (cue && source !== cue.kind) return cue.settings ?? {}
	if (cue) cues.delete(id)
	if (!source) return lastSettings.get(id) ?? {}
	const s = sourceValues(store.getState().live, source)
	lastSettings.set(id, s)
	return s
}

const paramsOf = id => {
	const s = draft ? { ...settingsFor(id), ...draft } : settingsFor(id)
	const value = key => s[key] ?? initial(key)
	return {
		run: value('run'),
		start: value('start'),
		in: value('in'),
		out: s.out ?? null,
		mode: value('mode'),
		direction: value('direction'),
		sync: value('sync'),
		beats: value('beats'),
		band: value('band'),
		gain: value('gain'),
		fall: value('fall'),
		envelope: s.envelope?.keys ? s.envelope : null,
		rate: override.rate ?? value('rate'),
		volume: override.volume ?? value('volume'),
	}
}

const staged = () => pool.stagedEntry(NODE)

/** The loop range inside the known duration: a range saved for a longer clip never points past this one's end. */
export const rangeOf = (p, duration) => {
	const hi = Math.min(p.out ?? Infinity, duration || Infinity)
	return { lo: Math.min(p.in, hi), hi }
}

const endOf = (p, video) => rangeOf(p, video.duration).hi

// The playhead and the start cue never leave the loop range.
const clampToRange = (t, p, video) => {
	const { lo, hi } = rangeOf(p, video.duration)
	return Math.max(lo, Math.min(t, hi))
}

const sign = p => (p.direction === 'reverse' ? -1 : 1)

/** Beat-locked: the tempo times in→out; only a repeating mode has a grid to lock to. */
const isBeat = p => p.sync === 'bpm' && FILE_CAPABILITIES.syncModes.includes(p.mode)

/** What a pass reaching its far end does: wrap to its near end, turn back, or stop. An empty range stops — it would wrap onto its end forever. Yoyo once turns after its first pass. */
const atFarEnd = (p, video) => {
	const { lo, hi } = rangeOf(p, video.duration)
	if (hi - lo <= END_EPSILON_S) return 'stop'
	if (p.mode === 'loop') return 'wrap'
	if (p.mode === 'pingpong_loop' || (p.mode === 'pingpong' && pass === sign(p))) return 'turn'
	return 'stop'
}

const stopAt = e => {
	e.video.pause()
	doneId = e.id
	store.dispatch(setHasEnded(true))
	apply()
}

const reachEnd = (e, p) => {
	const v = e.video
	const { lo, hi } = rangeOf(p, v.duration)
	const what = atFarEnd(p, v)
	if (what === 'stop') return stopAt(e)
	if (what === 'turn') {
		pass = -pass
		head = pass > 0 ? lo : hi
		return apply()
	}
	head = pass > 0 ? lo : hi
	if (pass > 0 && !v.loop) {
		v.currentTime = lo
		if (v.paused) v.play().catch(() => {})
	}
}

// Seconds of drift a beat-locked file seeks across rather than catching up by rate; the rate nudges by at most this fraction.
const BEAT_SEEK_S = 0.25
const BEAT_NUDGE = 0.1
// What a `<video>` takes as a playback rate.
const MIN_RATE = 0.0625
const MAX_RATE = 16

const clampRate = r => Math.min(MAX_RATE, Math.max(MIN_RATE, r))

/** The element time at position `f` along in→out, through the envelope. */
const timeAt = (p, lo, hi, f) => lo + (hi - lo) * (p.envelope ? sampleEnvelope(p.envelope.keys, f) : f)

/** The position along in→out that plays time `t`: through the envelope, its first. */
const positionOfTime = (p, lo, hi, t) => positionOfValue({ in: lo, out: hi, envelope: p.envelope }, t)

/** The start cue is the loop's phase: where a beat-locked pass sits on the grid's zero. */
const phaseCycles = (p, lo, hi) =>
	hi > lo ? cyclesAtPosition(p, (Math.max(lo, Math.min(hi, p.start)) - lo) / (hi - lo)) : 0

const seekTo = (v, at) => {
	if (!v.paused) v.pause()
	if (!v.seeking && Math.abs(v.currentTime - at) > 1e-3) v.currentTime = at
}

/** In→out spans `beats` on the tempo's grid, from the start cue. Rising straight passes play natively at the rate that fits, nudged onto the grid; falling or shaped ones and a stopped tempo seek. */
const beatTick = (v, p, t) => {
	const { lo, hi } = rangeOf(p, v.duration)
	const beatCycles = tempo.beatAt(t) / p.beats + phaseCycles(p, lo, hi)
	const f = positionAt(p, beatCycles)
	lastPos = f
	const at = timeAt(p, lo, hi, f)
	const rising = !p.envelope && positionAt(p, beatCycles + 1e-4) > f
	if (rising && tempo.snapshot().playing) {
		const drift = at - v.currentTime
		if (Math.abs(drift) > BEAT_SEEK_S && !v.seeking) v.currentTime = at
		const nudge = Math.max(-BEAT_NUDGE, Math.min(BEAT_NUDGE, drift))
		const rate = clampRate(((hi - lo) / (p.beats * tempo.beatSeconds())) * (1 + nudge))
		if (Math.abs(v.playbackRate - rate) > 1e-3) v.playbackRate = rate
		if (v.paused) v.play().catch(() => {})
		return
	}
	seekTo(v, at)
}

// The audio follower's average and position, kept across ticks.
const follower = {}

/** The playhead follows an audio band, swinging from the start cue; the element holds paused and seeks. */
const audioTick = (v, p, t) => {
	const { lo, hi } = rangeOf(p, v.duration)
	const rest = hi > lo ? (clampToRange(p.start, p, v) - lo) / (hi - lo) : 0
	lastPos = followLevel({ ...p, rest }, follower, bandLevel(p.band, t), t)
	seekTo(v, timeAt(p, lo, hi, lastPos))
}

/** A shaped pass on its own clock: `rate` × in→out's length per cycle, through the envelope; a one-shot stops at its last cycle. */
const shapedTick = (e, p, dt) => {
	const v = e.video
	const { lo, hi } = rangeOf(p, v.duration)
	if (hi - lo <= END_EPSILON_S) return stopAt(e)
	if (cycles == null) cycles = cyclesAtPosition(p, positionOfTime(p, lo, hi, v.currentTime))
	cycles += (dt * p.rate) / (hi - lo)
	const last = ONE_SHOT_CYCLES[p.mode]
	const done = last != null && cycles >= last
	if (done) cycles = last
	lastPos = positionAt(p, cycles)
	const at = timeAt(p, lo, hi, lastPos)
	if (!done) return seekTo(v, at)
	v.currentTime = at
	stopAt(e)
}

/** The clock's step for what the element cannot play itself: an audio drive, a beat-locked loop, a shaped pass, or a reverse pass of `rate` seconds per second, seeking only once the last seek has landed. */
const clockTick = t => {
	const e = staged()
	const dt = lastTickAt == null ? 0 : (t - lastTickAt) / 1000
	lastTickAt = t
	if (!e || e.video.readyState < 1) return
	const v = e.video
	const p = paramsOf(e.id)
	if (p.sync === 'fft') return audioTick(v, p, t)
	if (doneId === e.id) return
	if (isBeat(p)) return beatTick(v, p, t)
	if (p.envelope) return shapedTick(e, p, dt)
	if (!v.paused) v.pause()
	head -= dt * p.rate
	const { lo } = rangeOf(p, v.duration)
	if (head <= lo) {
		head = lo
		v.currentTime = lo
		return reachEnd(e, p)
	}
	if (!v.seeking) v.currentTime = head
}

const clockFor = on => {
	if (on && !offClock) {
		lastTickAt = null
		head = staged()?.video.currentTime ?? 0
		offClock = onTick('playhead', clockTick)
	} else if (!on && offClock) {
		offClock()
		offClock = null
	}
}

const apply = () => {
	const e = staged()
	if (!e) {
		clockFor(false)
		needAudio(false, 'playhead')
		return publish(null)
	}
	const v = e.video
	const p = paramsOf(e.id)
	publish(p)
	const key = `${e.id}:${p.direction}:${p.mode}`
	if (key !== passKey) {
		passKey = key
		pass = sign(p)
		cycles = null
	}
	const beat = isBeat(p)
	if (seeks.has(e.id) && v.readyState >= 1) {
		seeks.delete(e.id)
		v.currentTime = clampToRange(p.start, p, v)
		if (doneId === e.id) doneId = null
	}
	// A moved in or out pushes the playhead along.
	if (v.readyState >= 1) {
		const t = clampToRange(v.currentTime, p, v)
		if (Math.abs(t - v.currentTime) > 1e-3) v.currentTime = t
	}
	if (!beat && v.playbackRate !== p.rate) v.playbackRate = p.rate
	const volume = p.volume / 100
	if (Math.abs(v.volume - volume) > 1e-3) v.volume = volume
	// The whole clip loops natively, seamlessly; a sub-range wraps in `tick`.
	v.loop = !beat && !p.envelope && pass > 0 && p.mode === 'loop' && p.in <= 0 && p.out == null
	const looping = p.mode === 'loop'
	if (store.getState().videoFile.isLooping !== looping) store.dispatch(setLooping(looping))
	// An audio drive follows whether or not it plays: it has no transport.
	const audio = p.sync === 'fft'
	needAudio(audio, 'playhead')
	const clocked = audio || (p.run && (beat || !!p.envelope || pass < 0))
	clockFor(audio || (clocked && doneId !== e.id))
	if (!p.run) {
		doneId = null
		if (!v.paused) v.pause()
	} else if (!clocked && v.paused && doneId !== e.id) {
		pool.resumeAudio()
		v.play().catch(() => {})
	}
}

/** A native forward pass reaching its far end. */
const tick = () => {
	const e = staged()
	if (!e || e.video.paused) return
	const v = e.video
	const p = paramsOf(e.id)
	if (isBeat(p) || p.envelope || pass < 0) return
	const end = endOf(p, v)
	if (!Number.isFinite(end) || v.currentTime < end - END_EPSILON_S) return
	reachEnd(e, p)
}

let detach = null

// rVFC is frame-accurate while the tab is visible; `timeupdate` (~4 Hz) keeps the range in a hidden one.
const attach = () => {
	detach?.()
	detach = null
	const e = staged()
	if (!e) return
	const v = e.video
	let frameId = 0
	const onFrame = () => {
		tick()
		frameId = v.requestVideoFrameCallback(onFrame)
	}
	frameId = v.requestVideoFrameCallback(onFrame)
	// `tick` can miss the last frame (a 24fps clip's ends under END_EPSILON_S early; a hidden tab ticks at ~4 Hz): a loop wraps here instead.
	const onEnded = () => {
		const p = paramsOf(e.id)
		if (isBeat(p) || p.envelope || pass < 0) return
		if (p.run) reachEnd(e, p)
		else stopAt(e)
	}
	v.addEventListener('timeupdate', tick)
	v.addEventListener('loadedmetadata', apply)
	v.addEventListener('ended', onEnded)
	detach = () => {
		v.cancelVideoFrameCallback(frameId)
		v.removeEventListener('timeupdate', tick)
		v.removeEventListener('loadedmetadata', apply)
		v.removeEventListener('ended', onEnded)
	}
}

/** Writes the on-stage file's params into its fill source's bag, in one write. */
const writeParams = (values, meta) => {
	const source = staged() && sourceOf(staged().id)
	if (!source) return
	const payload = { source, values: { ...sourceValues(store.getState().live, source), ...values } }
	store.dispatch({ type: 'live/setSourceValues', payload, meta })
}

const draftChanged = () => {
	for (const fn of draftListeners) fn()
	apply()
}

/** Drag frames: played at once, written to Redux by `commitParams`. */
export const draftParams = values => {
	draft = { ...draft, ...values }
	draftChanged()
}

/** A drag's end: the draft and the release's own `values` as one write. `skipHistory` when the gesture pushed its own entry (a slider's mousedown). */
export const commitParams = (values, { skipHistory = false } = {}) => {
	const all = { ...draft, ...values }
	draft = null
	if (Object.keys(all).length) writeParams(all, skipHistory ? { skipHistory: true } : undefined)
	draftChanged()
}

/** The drag's uncommitted params; re-read on `onDraft`. */
export const currentDraft = () => draft

export const onDraft = fn => {
	draftListeners.add(fn)
	return () => draftListeners.delete(fn)
}

/** Writes one of the on-stage file's params. `transient`: a drag frame, held in the draft. */
export const writeParam = (key, value, { transient = false } = {}) =>
	transient ? draftParams({ [key]: value }) : writeParams({ [key]: value })

let seekedAt = 0

/** When the user last moved the playhead; playback, loop wraps and cues never count. */
export const lastSeek = () => seekedAt

/** The live playhead, never saved, held inside the loop range. Leaving a held `once` end re-arms it. */
export const seek = time => {
	const e = staged()
	if (!e) return
	seekedAt = performance.now()
	const v = e.video
	head = Math.max(0, clampToRange(time, paramsOf(e.id), v))
	v.currentTime = head
	cycles = null
	lastPos = null
	if (doneId === e.id) {
		doneId = null
		store.dispatch(setHasEnded(false))
		apply()
	}
}

export const seekBy = delta => {
	const v = staged()?.video
	if (v) seek(v.currentTime + delta)
}

export const currentTime = () => staged()?.video.currentTime ?? 0

/** Where the staged file sits along in→out (0…1), for the envelope's cursor: the position a shaped, beat-locked or audio pass last played, else the playhead's own; undefined with nothing staged. */
export const loopPosition = () => {
	const e = staged()
	if (!e) return undefined
	const p = paramsOf(e.id)
	const { lo, hi } = rangeOf(p, e.video.duration)
	if (!(hi > lo)) return undefined
	if (lastPos != null && (p.envelope || p.sync === 'fft' || isBeat(p))) return lastPos
	const t = e.video.currentTime
	return p.envelope ? positionOfTime(p, lo, hi, t) : Math.max(0, Math.min(1, (t - lo) / (hi - lo)))
}

/** Play writes `run` and the direction; a finished one-shot, or a playhead at the end it would run into, starts over from its near end. */
export const play = (direction = null) => {
	const e = staged()
	if (!e) return
	pool.resumeAudio()
	const p = paramsOf(e.id)
	const dir = direction ?? p.direction
	const { lo, hi } = rangeOf(p, e.video.duration)
	const t = e.video.currentTime
	const atEnd = dir === 'reverse' ? t <= lo + END_EPSILON_S : t >= hi - END_EPSILON_S
	// A shaped pass may end anywhere: only a finished one starts over, from its first cycle.
	if (!isBeat(p) && (doneId === e.id || (atEnd && !p.envelope))) {
		pass = dir === 'reverse' ? -1 : 1
		seek(dir === 'reverse' ? hi : lo)
		if (p.envelope) cycles = 0
	}
	const patch = {}
	if (!p.run) patch.run = true
	if (dir !== p.direction) patch.direction = dir
	if (Object.keys(patch).length) writeParams(patch)
	else apply()
}

export const pause = () => writeParam('run', false)

// A clock-driven pass holds the element paused while it plays: `run` says whether it plays.
export const togglePlay = () => {
	const e = staged()
	if (!e) return
	if (paramsOf(e.id).run && doneId !== e.id) pause()
	else play()
}

/** Back to the start cue, live. */
export const restart = () => {
	const e = staged()
	if (e) seek(paramsOf(e.id).start)
}

/** Sets `in` or `out` at the live playhead, on a frame, never past the other end. */
export const markAt = name => {
	const e = staged()
	if (!e) return
	const v = e.video
	const p = paramsOf(e.id)
	const t = v._fps ? Math.round(v.currentTime * v._fps) / v._fps : v.currentTime
	writeParam(name, name === 'in' ? Math.min(t, endOf(p, v)) : Math.max(t, rangeOf(p, v.duration).lo))
}

/** One frame, pausing first. */
export const step = dir => {
	const v = staged()?.video
	if (!v?._fps) return
	if (!v.paused) pause()
	seek(v.currentTime + dir / v._fps)
}

export const setMuted = on => {
	pool.setMuted(on)
	store.dispatch(setMutedFlag(on))
}

/** A preset load putting `id` on stage (resolved from its saved `kind`): seek to its `start`, and its params govern until Redux names the file. */
export const cue = (id, kind, settings) => {
	dropCues()
	cues.set(id, { kind, settings })
	seeks.add(id)
	if (staged()?.id === id) apply()
}

/** A same-file fade's tweened values for this frame; null at the fade's settle. */
export const setOverride = values => {
	override = values ?? {}
	apply()
}

/** One node, one pending cue: any other selection abandons it (a reversed or interrupted fade, a library pick). */
export const dropCues = () => {
	cues.clear()
	seeks.clear()
}

// MinimalRow drives playback only through this bridge.
const bridge = {
	togglePlayPause: togglePlay,
	handleSeek: seek,
	toggleLoop: () => {
		const e = staged()
		if (e) writeParam('mode', paramsOf(e.id).mode === 'loop' ? 'once' : 'loop')
	},
	toggleMute: () => setMuted(!pool.isMuted()),
	handleSpeedChange: rate => writeParam('rate', rate),
	setVolume: (percent, transient) => writeParam('volume', percent, { transient }),
	// A slider's mousedown pushed the gesture's history entry.
	commitDraft: () => commitParams({}, { skipHistory: true }),
}

let started = false
let lastBags = null
let lastObjects = null

/** Follows pool node `id`'s stage and its file's params from now on. */
export const startPlayhead = id => {
	if (started) return
	started = true
	NODE = id
	pool.subscribe(type => {
		if (type !== 'stage') return
		attach()
		window.videoTransport = staged() ? bridge : null
		apply()
	})
	store.subscribe(() => {
		const { source_settings, objects } = store.getState().live
		if (source_settings === lastBags && objects === lastObjects) return
		lastBags = source_settings
		lastObjects = objects
		apply()
	})
}

if (IS_DEV) window.playhead = { params: () => staged() && paramsOf(staged().id), cues: () => Object.fromEntries(cues) }
